import { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { AuthContext } from "../auth/AuthContext";
import DailyNotebookModal from "../components/DailyNotebookModal";
import { getProjects, getMyProjects } from "../api/projectApi";
import { DEFAULT_DEPARTMENT, normalizeDepartment } from "../constants/departments";
import { canDeleteWorkItems } from "../constants/sprintAccess";
import { getStoredDepartmentView } from "../components/DepartmentSwitcher";
import { useDepartmentWorkflow } from "../hooks/useDepartmentWorkflow";
import { normalizeAppRole } from "../utils/roleUtils";

export default function DailyNotebook() {
  const { user, role } = useContext(AuthContext);
  const currentRole = normalizeAppRole(role);
  const canDelete = canDeleteWorkItems(currentRole);
  const departmentView = getStoredDepartmentView();
  const userDepartment = normalizeDepartment(user?.department || DEFAULT_DEPARTMENT);
  const effectiveDepartment = departmentView || userDepartment;
  const { departmentUsesSprints } = useDepartmentWorkflow();

  const [selectedProject, setSelectedProject] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const { data: projects = [], isLoading, isError } = useQuery({
    queryKey: ["projects", currentRole, departmentView || "all", "daily-notebook"],
    queryFn: () =>
      (["superadmin", "admin"].includes(currentRole)
        ? getProjects()
        : getMyProjects()
      ).then((r) => r.data?.projects || r.data || []),
  });

  const scopedProjects = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    if (!departmentView) return list;
    return list.filter((p) => normalizeDepartment(p.department) === normalizeDepartment(departmentView));
  }, [projects, departmentView]);

  const selectedProjectRow = useMemo(
    () => scopedProjects.find((p) => String(p.projectid) === String(selectedProject)),
    [scopedProjects, selectedProject]
  );

  useEffect(() => {
    if (selectedProject) setModalOpen(true);
  }, [selectedProject]);

  if (departmentUsesSprints(effectiveDepartment)) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: "auto" }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Daily Notebook for sprint teams is available from Sprint Board — select a sprint and open
          the notebook from the header.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: "auto" }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
        Daily Notebook
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A simple day-wise log. Type <strong>@name</strong> to assign work — tasks land in My Tasks.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, textAlign: "center" }}>
        <TextField
          select
          label="Project"
          size="small"
          fullWidth
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          disabled={isLoading || scopedProjects.length === 0}
          sx={{ mb: 2, textAlign: "left" }}
        >
          <MenuItem value="">— Select project —</MenuItem>
          {scopedProjects.map((p) => (
            <MenuItem key={p.projectid} value={p.projectid}>
              {p.projectname || p.projectid}
            </MenuItem>
          ))}
        </TextField>

        {isError && (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            Could not load projects.
          </Alert>
        )}
        {!isLoading && scopedProjects.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
            No projects in {departmentView || "your department"}.
          </Alert>
        )}

        <Button
          variant="contained"
          disabled={!selectedProject}
          onClick={() => setModalOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700, px: 3 }}
        >
          Open notebook
        </Button>
      </Paper>

      <DailyNotebookModal
        open={modalOpen && !!selectedProject}
        onClose={() => setModalOpen(false)}
        projectId={selectedProject}
        sprintId={null}
        projectName={selectedProjectRow?.projectname || selectedProject}
        canEdit={Boolean(selectedProject)}
        canManageTasks={Boolean(selectedProject)}
        canDelete={canDelete}
        department={effectiveDepartment}
      />
    </Box>
  );
}
